import { styled, css } from 'styled-components';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 1rem;
`;

export const ErrorBox = styled.div`
  ${({ theme: { colors, fontSizes } }) => css`
    background: ${colors.semanticDangerTint90};
    border: 1px solid ${colors.semanticDangerTint80};
    border-radius: 0.5rem;
    padding: 0.5rem 0.75rem;

    p {
      color: ${colors.semanticDangerShade20};
      font-size: ${fontSizes.s12};
      margin: 0 !important;
    }
  `}
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

export const Section = styled.div`
  ${({ theme: { colors } }) => css`
    border-radius: 0.5rem;
    box-shadow:
      0 0 0 1px ${colors.brandDarkTint90},
      0 0.375rem 0.25rem -0.125rem ${colors.brandDarkAlpha5};
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: 0.75rem 0.5rem;
    width: 100%;
  `}
`;

export const SectionHeader = styled.button<{ $isOpen: boolean }>`
  ${({ theme: { colors, fontSizes }, $isOpen }) => css`
    align-items: center;
    background: transparent;
    border: none;
    cursor: pointer;
    display: flex;
    gap: 0.25rem;
    padding: 0 0.5rem;
    place-self: flex-start;
    user-select: none;

    span {
      color: ${colors.brandDarkTint30};
      font-size: ${fontSizes.s12};
      font-weight: 600;
    }

    .icon {
      flex: 0 0 auto;
      height: 1rem;
      width: 1rem;
      transition: transform 0.15s ease-in-out;

      ${
        $isOpen &&
        css`
          transform: rotate(90deg);
        `
      }
    }
  `}
`;

export const SectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0 0.5rem;
  overflow: hidden;
`;

export const PropertyRow = styled.div`
  ${() => css`
    align-items: flex-start;
    display: flex;
    gap: 0.25rem;
    padding: 0.5rem 0;
    overflow: hidden;

    .ds-tooltip-wrapper {
      line-height: 1em;
    }

    .icon {
      height: 1rem;
      width: 1rem;
    }
  `}
`;

export const PropertyLabel = styled.span`
  ${({ theme: { colors, fontSizes } }) => css`
    color: ${colors.brandDarkTint30};
    flex: 0 0 5.5rem;
    font-size: ${fontSizes.s12};
    font-weight: 500;
    user-select: none;
  `}
`;

export const PropertyValue = styled.div<{ $column?: boolean }>`
  ${({ theme: { colors, fontSizes }, $column }) => css`
    align-items: ${$column ? 'flex-start' : 'center'};
    display: flex;
    flex: 1;
    flex-direction: ${$column ? 'column' : 'row'};
    flex-wrap: wrap;
    gap: ${$column ? '0.125rem' : '0.5rem'};
    min-width: 0;
    place-self: center;

    span {
      color: ${colors.brandDark};
      display: block;
      font-size: ${fontSizes.s12};
      max-width: 100%;
      min-width: 0;
      overflow-wrap: anywhere;
      white-space: normal;
      word-break: break-word;
    }
  `}
`;

export const StatusIconWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  height: 1rem;
  isolation: isolate;
  justify-content: center;
  position: relative;
  width: 1rem;

  .icon {
    height: 1rem;
    width: 1rem;
  }
`;

export const CampaignRow = styled.div`
  align-items: center;
  display: flex;
  gap: 0.5rem;
`;

export const InitialsAvatar = styled.div<{ $color: string; $isDark: boolean }>`
  ${({ theme: { colors, fontWeights }, $color, $isDark }) => css`
    align-items: center;
    background: ${$color};
    border-radius: 1.25rem;
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    height: 1.25rem;
    position: relative;
    user-select: none;
    width: 1.25rem;

    &::before {
      border-radius: inherit;
      box-shadow: inset 0 0 0 1px ${colors.brandDarkAlpha20};
      content: '';
      display: block;
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      user-select: none;
      width: 100%;
      z-index: 1;
    }

    span {
      color: ${$isDark ? colors.brandLightAlpha90 : colors.brandDarkAlpha80};
      display: inline-block;
      font-size: 0.625rem;
      font-weight: ${fontWeights.semiBold};
      line-height: 1em;
    }
  `}
`;

export const CaptionText = styled.p<{ $scrollable?: boolean }>`
  ${({ $scrollable, theme: { colors, fontSizes } }) => css`
    color: ${colors.brandDark};
    border-radius: 0.25rem;
    font-size: ${fontSizes.s12};
    line-height: 1.25em;
    margin: -0.25rem -0.5rem;
    max-height: 6.5rem;
    overflow-y: auto;
    padding: 0.25rem 0.5rem;
    white-space: pre-wrap;
    width: 100%;
    word-break: break-word;

    a {
      word-break: break-all;
    }

    ${
      $scrollable &&
      css`
        &:hover {
          outline: 1px solid ${colors.brandDarkTint90};
          padding-bottom: 0.5rem;
        }
      `
    }

    &::-webkit-scrollbar {
      height: 6px;
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 6px;
      height: 6px;
      -webkit-border-radius: 6px;
    }
  `}
`;

export const MediaGrid = styled.div`
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(4, 1fr);
  user-select: none;
  width: 100%;
`;

export const MediaThumb = styled.div`
  ${({ theme: { colors } }) => css`
    align-items: center;
    aspect-ratio: 1 / 1;
    background: ${colors.brandDarkAlpha10};
    border-radius: 0.5rem;
    cursor: pointer;
    display: flex;
    justify-content: center;
    overflow: hidden;
    position: relative;
    width: 100%;

    img,
    canvas {
      height: 100%;
      left: 0;
      object-fit: cover;
      position: absolute;
      top: 0;
      width: 100%;
    }
  `}
`;

export const ThumbHighlight = styled.div`
  ${({ theme: { colors, fontWeights } }) => css`
    align-items: flex-end;
    background: ${`linear-gradient(0deg, ${colors.brandDarkAlpha70} 0%, rgba(0, 0, 0, 0) 100%)`};
    bottom: 0;
    display: flex;
    height: 100%;
    justify-content: flex-end;
    left: 0;
    padding: 0.375rem;
    position: absolute;
    width: 100%;
    z-index: 1;

    .icon {
      height: 0.875rem;
      width: 0.875rem;
    }

    span {
      color: ${colors.brandLight};
      font-size: 0.625rem;
      font-weight: ${fontWeights.medium};
      line-height: 1em;
    }
  `}
`;

export const MediaOverflow = styled.div`
  ${({ theme: { colors } }) => css`
    aspect-ratio: 1 / 1;
    background: ${colors.brandDark};
    border-radius: 0.5rem;
    overflow: hidden;
    position: relative;
    width: 100%;

    img,
    canvas {
      height: 100%;
      left: 0;
      object-fit: cover;
      position: absolute;
      top: 0;
      width: 100%;
    }
  `}
`;

export const OverflowMask = styled.div`
  ${({ theme: { colors } }) => css`
    align-items: center;
    background: rgba(0, 0, 0, 0.6);
    border-radius: 0.5rem;
    bottom: 0;
    color: ${colors.brandLight};
    display: flex;
    font-size: 0.875rem;
    font-weight: 700;
    justify-content: center;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 1;
  `}
`;

export const NetworkIconsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  place-self: center;
  user-select: none;

  .icon {
    height: 1rem;
    width: 1rem;
  }
`;

export const ProfileRow = styled.div`
  align-items: center;
  display: flex;
  gap: 0.75rem;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const ProfileAvatarWrapper = styled.div`
  ${({ theme: { colors } }) => css`
    align-items: center;
    background: ${colors.brandDarkAlpha10};
    border-radius: 0.5rem;
    display: flex;
    flex: 0 0 auto;
    height: 2.5rem;
    justify-content: center;
    overflow: visible;
    position: relative;
    width: 2.5rem;

    img {
      border-radius: 0.5rem;
      height: 100%;
      object-fit: cover;
      width: 100%;
    }
  `}
`;

export const ProfileNetworkBadge = styled.div`
  ${({ theme: { colors } }) => css`
    background: ${colors.brandLight};
    border-radius: 50%;
    bottom: -0.25rem;
    display: flex;
    padding: 0.125rem;
    position: absolute;
    right: -0.25rem;

    .icon {
      height: 0.75rem;
      width: 0.75rem;
    }
  `}
`;

export const ProfilesSectionHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 0 0.5rem;
  width: 100%;
`;

export const ProfilesToggleButton = styled.button<{ $isOpen: boolean }>`
  ${({ theme: { colors, fontSizes }, $isOpen }) => css`
    align-items: center;
    background: transparent;
    border: none;
    cursor: pointer;
    display: flex;
    gap: 0.25rem;
    padding: 0;

    span {
      color: ${colors.brandDarkTint30};
      font-size: ${fontSizes.s12};
      font-weight: 600;
    }

    .icon {
      flex: 0 0 auto;
      height: 1rem;
      width: 1rem;
      transition: transform 0.15s ease-in-out;

      ${
        $isOpen &&
        css`
          transform: rotate(90deg);
        `
      }
    }
  `}
`;

export const MarkAllButton = styled.button`
  ${({ theme: { colors, fontSizes } }) => css`
    background: transparent;
    border: none;
    color: ${colors.brandPrimary};
    cursor: pointer;
    font-size: ${fontSizes.s12};
    font-weight: 600;
    padding: 0;
    white-space: nowrap;
  `}
`;

export const FilterTabs = styled.div`
  border-radius: 1.5rem;
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem 0;
  margin-left: -0.5rem;
  width: calc(100% + 1rem);
`;

export const FilterTab = styled.button<{ $active: boolean }>`
  ${({ theme: { colors, fontSizes }, $active }) => css`
    background: ${$active ? colors.brandDarkTint95 : 'transparent'};
    border: none;
    border-radius: 1.5rem;
    color: ${colors.brandDark};
    cursor: pointer;
    flex: 1;
    font-size: ${fontSizes.s12};
    font-weight: 500;
    padding: 0.5rem;
    transition: background 0.15s ease;
    white-space: nowrap;
  `}
`;
